import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { SINGLE_DOT_FRETS, DOUBLE_DOT_FRETS } from './GuitarNeckVisualizer.utils';

const FretboardHeader = ({ startFret, numFretsToShow, dotsPosition = "bottom", hideStringColumn = false, className = "", onFretClick, highlightedFrets = [], leftHanded = false }) => {
  // Fixed width for each fret - must match the width in StringRow
  const FRET_WIDTH = 60; // 60px per fret
  // Render content based on dotsPosition prop
  const renderContent = () => {
    const fretNumbers = (
      <Box 
        className="fretboard-header-numbers-container"
        sx={{ 
          display: 'flex', 
          flexDirection: leftHanded ? 'row-reverse' : 'row',
          minWidth: `${(numFretsToShow + 1) * FRET_WIDTH}px` // +1 to account for the nut/open string
        }}>
        {/* String label column - only show if not hidden */}
        {!hideStringColumn && (
          <Box 
            className="fretboard-header-string-column"
            sx={{ 
              width: '30px', 
              textAlign: 'center',
              borderRight: leftHanded ? 'none' : '1px solid rgba(255, 255, 255, 0.15)',
              borderLeft: leftHanded ? '1px solid rgba(255, 255, 255, 0.15)' : 'none',
              flexShrink: 0
            }}>
          </Box>
        )}
        
        {/* Fret numbers */}
        {Array.from({ length: numFretsToShow + 1 }).map((_, index) => {
          const actualFret = startFret + index;
          return (
            <Box 
              key={index} 
              className={`fretboard-header-fret-cell fret ${highlightedFrets.includes(actualFret) ? 'highlighted' : ''}`}
              sx={{
                width: `${FRET_WIDTH}px`, // Use the constant for consistency
                flexShrink: 0, // Prevent shrinking
                textAlign: 'center',
                borderRight: leftHanded ? 'none' : '1px solid rgba(255, 255, 255, 0.1)',
                borderLeft: leftHanded ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                pb: dotsPosition === "bottom" ? 1 : 0,
                pt: dotsPosition === "top" ? 1 : 0,
                position: 'relative',
                backgroundColor: highlightedFrets.includes(actualFret) ? 'rgba(33, 150, 243, 0.3)' : 'transparent',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: highlightedFrets.includes(actualFret) ? 'rgba(33, 150, 243, 0.4)' : 'rgba(255, 255, 255, 0.05)'
                },
                transition: 'background-color 0.2s ease'
              }}
              onClick={() => {
                // Call the parent callback if provided
                if (onFretClick) {
                  onFretClick(actualFret);
                }
              }}
            >
              <Typography
                className="fretboard-header-fret-number"
                variant="caption"
                sx={{
                  opacity: SINGLE_DOT_FRETS.includes(actualFret) || DOUBLE_DOT_FRETS.includes(actualFret) ? 1 : 0.5,
                  fontSize: '0.7rem',
                  fontWeight: DOUBLE_DOT_FRETS.includes(actualFret) ? 800 : SINGLE_DOT_FRETS.includes(actualFret) ? 700 : 400,
                }}
              >
                {actualFret}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
    
    const fretDots = (
      <Box 
        className="fretboard-header-dots-container"
        sx={{ 
          display: 'flex',
          flexDirection: leftHanded ? 'row-reverse' : 'row',
          minWidth: `${(numFretsToShow + 1) * FRET_WIDTH}px`, // +1 to account for the nut/open string
          height: '14px',
          position: 'relative',
          backgroundColor: '#121212',
        }}>
        {/* Empty cell for alignment - only show if not hidden */}
        {!hideStringColumn && (
          <Box className="fretboard-header-string-column-spacer" sx={{ width: '30px', flexShrink: 0 }}></Box>
        )}
        
        {/* Fret marker positions */}
        {Array.from({ length: numFretsToShow + 1 }).map((_, fretIndex) => {
          const actualFret = startFret + fretIndex;
          const isSingleDot = SINGLE_DOT_FRETS.includes(actualFret);
          const isDoubleDot = DOUBLE_DOT_FRETS.includes(actualFret);
          
          return (
            <Box 
              key={fretIndex}
              className={`fretboard-header-dot-cell fret ${isSingleDot ? 'single-dot' : ''} ${isDoubleDot ? 'double-dot' : ''} ${highlightedFrets.includes(actualFret) ? 'highlighted' : ''}`}
              sx={{
                width: `${FRET_WIDTH}px`, // Use the constant for consistency
                flexShrink: 0, // Prevent shrinking
                position: 'relative',
                borderRight: leftHanded ? 'none' : '1px solid rgba(255, 255, 255, 0.1)',
                borderLeft: leftHanded ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                backgroundColor: highlightedFrets.includes(actualFret) ? 'rgba(33, 150, 243, 0.3)' : 'transparent',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: highlightedFrets.includes(actualFret) ? 'rgba(33, 150, 243, 0.4)' : 'rgba(255, 255, 255, 0.05)'
                },
                transition: 'background-color 0.2s ease'
              }}
              onClick={() => {
                // Call the parent callback if provided
                if (onFretClick) {
                  onFretClick(actualFret);
                }
              }}
            >
              {isSingleDot && (
                <Box
                  className="fretboard-header-dot-marker single"
                  sx={{
                    position: 'absolute',
                    width: '7px',
                    height: '7px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255, 255, 255, 0.85)',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              )}

              {isDoubleDot && (
                <>
                  <Box
                    className="fretboard-header-dot-marker double left"
                    sx={{
                      position: 'absolute',
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      top: '50%',
                      left: '35%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  />
                  <Box
                    className="fretboard-header-dot-marker double right"
                    sx={{
                      position: 'absolute',
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      top: '50%',
                      left: '65%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  />
                </>
              )}
            </Box>
          );
        })}
      </Box>
    );
    
    // Return elements in the correct order based on dotsPosition
    return dotsPosition === "top" ? 
      <>{fretDots}{fretNumbers}</> : 
      <>{fretNumbers}{fretDots}</>;
  };
  
  // Wrap the content in a container with the custom className
  return (
    <Box className={`fretboard-header ${className}`}>
      {renderContent()}
    </Box>
  );
};

export default FretboardHeader;
